import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { resetShimmer } from '@theme/utilities';

/**
 * @param {HTMLElement} container
 * @param {boolean} isSubscribe
 */
function setToggleState(container, isSubscribe) {
  const toggle = container.querySelector('.dcoded-cart-subscribe__toggle');
  if (!toggle) return;

  toggle.classList.toggle('is-subscribe-active', isSubscribe);
  toggle.classList.toggle('is-onetime-active', !isSubscribe);

  container.querySelectorAll('.dcoded-cart-subscribe__option').forEach((option) => {
    if (!(option instanceof HTMLElement)) return;
    const isSubscribeOption = option.classList.contains('dcoded-cart-subscribe__option--subscribe');
    option.classList.toggle('is-active', isSubscribe ? isSubscribeOption : !isSubscribeOption);
  });
}

/**
 * @param {HTMLElement} container
 * @param {string} message
 */
function showError(container, message) {
  let errorEl = container.querySelector('.dcoded-cart-subscribe__error');
  if (!errorEl) {
    errorEl = document.createElement('p');
    errorEl.className = 'dcoded-cart-subscribe__error';
    container.appendChild(errorEl);
  }
  errorEl.textContent = message;
}

/**
 * @param {HTMLElement} container
 */
function clearError(container) {
  container.querySelector('.dcoded-cart-subscribe__error')?.remove();
}

/**
 * @returns {string[]}
 */
function getSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.push(el.dataset.sectionId);
    }
  });
  return ids;
}

/**
 * @param {Record<string, any>} cartData
 * @param {string} sourceSectionId
 */
async function refreshCartFromResponse(cartData, sourceSectionId) {
  const sections = cartData.sections;

  if (!sections?.[sourceSectionId]) {
    window.location.reload();
    return;
  }

  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (!(component instanceof HTMLElement) || !component.dataset.sectionId) return;

    const sectionId = component.dataset.sectionId;
    const html = sections?.[sectionId];
    if (!html) return;

    morphSection(sectionId, html, component.dataset.drawer !== undefined ? 'hydration' : 'full');
    resetShimmer(component);
  });

  document.querySelectorAll('cart-quantity-selector-component').forEach((selector) => {
    /** @type {any} */ (selector).updateButtonStates?.();
  });

  window.__bhCartUpsell?.refreshUpsellPricesFromCart?.(cartData);

  document.querySelectorAll('.cart__checkout-button, #checkout').forEach((button) => {
    if (button instanceof HTMLButtonElement && button.closest('.cart-drawer, .cart-page')) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });

  document.querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.pointerEvents = '';
      el.style.opacity = '';
    }
  });

  const sourceComponent = document.querySelector(
    `cart-items-component[data-section-id="${sourceSectionId}"]`
  );

  if (sourceComponent instanceof HTMLElement) {
    const parsedSection = new DOMParser().parseFromString(sections[sourceSectionId], 'text/html');
    const newCartHiddenItemCount = parsedSection.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    sourceComponent.dispatchEvent(
      new CartUpdateEvent(cartData, sourceSectionId, {
        itemCount: newCartItemCount,
        source: 'dcoded-cart-subscribe',
        sections,
      })
    );
  }

  document.dispatchEvent(new CustomEvent('dcoded-cart-drawer-refreshed', { bubbles: true }));
}

/**
 * @param {HTMLElement} container
 * @param {boolean} isSubscribe
 * @returns {Promise<void>}
 */
async function changeSellingPlan(container, isSubscribe) {
  const line = parseInt(container.dataset.line || '', 10);
  const quantity = parseInt(container.dataset.quantity || '', 10) || 1;
  const sellingPlanId = container.dataset.sellingPlanId;
  const sections = getSectionIds();
  const cartItemsComponent = container.closest('cart-items-component');
  const sourceSectionId =
    cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : undefined;

  if (!line) {
    showError(container, 'Unable to update this item.');
    return;
  }

  if (isSubscribe && !sellingPlanId) {
    showError(container, 'Subscription is not available for this item.');
    return;
  }

  /** @type {Record<string, unknown>} */
  const payload = {
    line,
    quantity,
    selling_plan: isSubscribe ? parseInt(sellingPlanId, 10) : null,
  };

  if (sections.length) {
    payload.sections = sections.join(',');
    payload.sections_url = window.location.pathname;
  }

  container.classList.add('is-loading');
  container.querySelectorAll('.dcoded-cart-subscribe__input').forEach((input) => {
    input.disabled = true;
  });

  try {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || data.errors || data.description) {
      throw new Error(
        typeof data.errors === 'string'
          ? data.errors
          : data.description || data.message || 'Cart update failed.'
      );
    }

    clearError(container);

    if (sourceSectionId) {
      await refreshCartFromResponse(data, sourceSectionId);
    } else {
      window.location.reload();
    }
  } catch (error) {
    console.error('Dcoded cart subscribe:', error);
    const message = error instanceof Error ? error.message : 'Unable to update subscription.';
    showError(container, message);

    const wasSubscribed = container.dataset.subscribed === 'true';
    setToggleState(container, wasSubscribed);
    container.querySelectorAll('.dcoded-cart-subscribe__input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.dataset.purchaseType === (wasSubscribed ? 'subscription' : 'onetime');
      }
    });
  } finally {
    container.classList.remove('is-loading');
    container.querySelectorAll('.dcoded-cart-subscribe__input').forEach((input) => {
      input.disabled = false;
    });
  }
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  if (!input.matches('.dcoded-cart-subscribe__input')) return;

  const container = input.closest('[data-dcoded-subscribe]');
  if (!(container instanceof HTMLElement) || container.classList.contains('is-loading')) return;

  const isSubscribe = input.dataset.purchaseType === 'subscription';
  const wasSubscribed = container.dataset.subscribed === 'true';

  if (isSubscribe === wasSubscribed) return;

  setToggleState(container, isSubscribe);
  clearError(container);
  changeSellingPlan(container, isSubscribe);
});
